/**
 * Wishlist toast notification
 *
 * Listens for the wk:wishlist:add:success DOM event dispatched by Wishlist King
 * (and optionally by a Code Access bridge script — see bottom of this file),
 * then shows a brief toast card in the bottom-right corner.
 *
 * The toast element and all settings are owned by
 * snippets/dev-wishlist-toast.liquid (rendered globally in layout/theme.liquid).
 *
 * --- Wishlist King Code Access bridge ---
 * Wishlist King's internal event bus is not always exposed as a native DOM event.
 * If the toast does not appear, paste the following into the WK admin panel under
 * Apps → Wishlist King → Code Access → Custom JS:
 *
 *   export function setup({ app }) {
 *     app.events.subscribe('wk:wishlist:add:success', (payload) => {
 *       document.dispatchEvent(
 *         new CustomEvent('wk:wishlist:add:success', { detail: payload })
 *       );
 *     });
 *   }
 */

const toast = document.querySelector('[data-wishlist-toast]');
if (toast) {
  const delay = parseInt(toast.dataset.delay || '4', 10) * 1000;
  const imgWrap = toast.querySelector('[data-wt-img-wrap]');
  const imgEl = toast.querySelector('[data-wt-img]');
  const productEl = toast.querySelector('[data-wt-product]');
  const closeBtn = toast.querySelector('[data-wt-close]');

  let hideTimer = null;
  let hideAbort = null;

  /* ── Helpers ─────────────────────────────────────── */

  function resolveImageSrc(product, variant) {
    const candidates = [
      variant?.image?.src,
      variant?.featured_image?.src,
      product?.featured_image?.src,
      typeof product?.featured_image === 'string' ? product.featured_image : null,
      Array.isArray(product?.images) ? product.images[0]?.src : null,
      // Shopify Analytics meta — available on product pages
      window.ShopifyAnalytics?.meta?.product?.featured_image,
    ];

    for (const url of candidates) {
      if (url && typeof url === 'string' && (url.startsWith('http') || url.startsWith('//'))) {
        return url;
      }
    }

    return '';
  }

  function resolveProductText(product, variant) {
    const title = product?.title || '';
    const variantTitle = variant?.title || '';

    if (!title) return '';

    // Skip variants labelled "Default Title" (Shopify single-variant products)
    if (variantTitle && variantTitle !== 'Default Title') {
      return `${title}, ${variantTitle}`;
    }
    return title;
  }

  /* ── Show / hide ──────────────────────────────────── */

  function showToast({ product, variant } = {}) {
    // Abort any pending hide-cleanup listener so it doesn't wipe the new content
    if (hideAbort) {
      hideAbort.abort();
      hideAbort = null;
    }

    const imgSrc = resolveImageSrc(product, variant);
    const productText = resolveProductText(product, variant);

    // Image panel always visible (shows gray placeholder when no src)
    imgWrap.removeAttribute('hidden');
    if (imgSrc) {
      imgEl.src = imgSrc;
      imgEl.removeAttribute('hidden');
    } else {
      imgEl.setAttribute('hidden', '');
    }

    productEl.textContent = productText;

    // Announce to screen readers then reveal
    toast.removeAttribute('aria-hidden');
    toast.classList.remove('dev-wt--hiding');

    // Force a reflow so removing 'hiding' doesn't merge with adding 'visible'
    void toast.offsetHeight;

    toast.classList.add('dev-wt--visible');

    clearTimeout(hideTimer);
    hideTimer = setTimeout(hideToast, delay);
  }

  function hideToast() {
    clearTimeout(hideTimer);
    hideAbort = new AbortController();
    toast.classList.add('dev-wt--hiding');
    toast.setAttribute('aria-hidden', 'true');

    toast.addEventListener(
      'transitionend',
      () => {
        hideAbort = null;
        toast.classList.remove('dev-wt--visible', 'dev-wt--hiding');

        // Reset content so stale data isn't visible on next open
        productEl.textContent = '';
        imgEl.removeAttribute('src');
        imgEl.setAttribute('hidden', '');
      },
      { once: true, signal: hideAbort.signal }
    );
  }

  /* ── Event listeners ──────────────────────────────── */

  closeBtn?.addEventListener('click', hideToast);

  // Native DOM event dispatched by WK (or the Code Access bridge above)
  document.addEventListener('wk:wishlist:add:success', (e) => {
    const detail = e.detail || {};

    showToast({
      product: detail.product || null,
      variant: detail.variant || detail.selectedVariant || null,
    });
  });
}
